import type { TFunction } from 'i18next';

import { formatShortDate } from '@/app/ops/helpers/formatters';
import { desvioDoItem, desvioFraseCurta } from '@/domain/fila/helpers/explicaDesvio';
import type { FilaItem } from '@/domain/fila/interfaces/fila';

/** O mesmo ponto que separa a OP do produto no cabeçalho do cartão. */
const SEPARADOR = ' · ';

/**
 * Os FATOS do item, curtos, numa linha só.
 *
 * O cartão do dia mostrava a frase inteira que a API escreve (`porque`), e ela termina
 * dizendo o que fazer: "Veja a carga no Mapa, ou repactue a entrega". Logo abaixo o cartão
 * repetia a MESMA ação na sua própria linha. Lendo cinco cartões assim, a informação que
 * importa (qual OP, quanto falta, para quando) ficava enterrada na repetição.
 *
 * Aqui entram só os números e as datas, montados dos campos do item e nunca do texto da
 * API. A frase longa continua existindo, como dica na etiqueta do motivo.
 */
export function fatosDoItem(t: TFunction, item: FilaItem): string {
  return [fatoDoPrazo(t, item), fatoDoSaldo(t, item), fatoDoMotivo(t, item)]
    .filter((fato): fato is string => fato !== null && fato !== '')
    .join(SEPARADOR);
}

/** Vencida diz VENCEU; em dia diz só para quando é. */
function fatoDoPrazo(t: TFunction, item: FilaItem): string | null {
  const data = formatShortDate(item.entrega);
  if (data === null) {
    return null;
  }
  return item.vencida
    ? t('home.hoje.fatos.venceuEm', { data })
    : t('home.hoje.fatos.entregaEm', { data });
}

/** O que FALTA, que é por onde quem trata a fila decide. */
function fatoDoSaldo(t: TFunction, item: FilaItem): string | null {
  if (!Number.isFinite(item.saldo) || item.saldo <= 0) {
    return null;
  }
  return t('home.hoje.fatos.faltam', { count: item.saldo });
}

/**
 * O número que é do MOTIVO, quando ele tem um. Ritmo compara os dois tempos, máquina diz
 * qual parou e material diz para quando foi prometido: os outros motivos se explicam com
 * a etiqueta e não precisam de mais nada na linha.
 */
function fatoDoMotivo(t: TFunction, item: FilaItem): string | null {
  if (item.tipo === 'DESVIO') {
    const desvio = desvioDoItem(item);
    return desvio === null ? null : desvioFraseCurta(t, desvio);
  }
  if (item.tipo === 'MAQUINA_PARADA') {
    const tipo = (item.maquinaParada?.tipo ?? '').trim();
    return tipo === '' ? null : t('home.hoje.fatos.maquinaParada', { tipo });
  }
  if (item.tipo === 'MATERIA_PRIMA') {
    const data = formatShortDate(item.mpPrevistaPara);
    return data === null ? null : t('home.hoje.fatos.materialPara', { data });
  }
  return null;
}
